import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Grid,
  ToggleButtonGroup, ToggleButton, CircularProgress, Alert, Stack,
  Collapse, IconButton, Chip, Tooltip,
} from '@mui/material';
import {
  Refresh, ExpandMore, KeyboardArrowRight, Clear, Edit, Delete,
  CalendarMonth, Launch, Undo,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useWOv2 from './hooks/useWOv2';
import { useAuth } from '../../../contexts/AuthContext';
import usePageTitle from '../../../hooks/usePageTitle';
import { groupForConsolidatedView } from './utils/groupForConsolidatedView';
import WOv2Card from './components/WOv2Card';
import WOv2DetailDrawer from './components/WOv2DetailDrawer';
import GenerationModeBadge from './components/GenerationModeBadge';
import MigrateFromV1Button from './components/MigrateFromV1Button';
import AdminWOEditDialogV2 from './components/AdminWOEditDialogV2';
import AdminWODeleteDialogV2 from './components/AdminWODeleteDialogV2';
import AdminWOUndoCompletionDialogV2 from './components/AdminWOUndoCompletionDialogV2';

const STORAGE_KEY = 'wo_v2_view_mode';
const FILTERS_KEY = 'wo_v2_wo_mgmt_filters';
const GROUP_BY_DATE_KEY = 'wo_v2_wo_mgmt_group_by_date';
const SORT_KEY = 'wo_v2_wo_mgmt_sort';
const DEBOUNCE_MS = 400;

// 'active' is a client-side pseudo-status (planned / released / in progress) -
// the default view: people come here for outstanding work, not history.
const ACTIVE_STATUSES = ['planned', 'released', 'in_progress'];
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active (not finished)' },
  { value: '', label: 'All statuses' },
  { value: 'planned', label: 'Planned' },
  { value: 'released', label: 'Released' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SORT_OPTIONS = [
  { value: 'due_date_asc', label: 'Due soonest' },
  { value: 'due_date_desc', label: 'Due latest' },
  { value: 'item_asc', label: 'Item name A-Z' },
  { value: 'wo_number', label: 'WO number' },
  { value: 'qty_desc', label: 'Quantity high-low' },
];

function itemNameCompare(a, b) {
  const na = (a.output_item_name || '').toLowerCase();
  const nb = (b.output_item_name || '').toLowerCase();
  if (na !== nb) return na.localeCompare(nb);
  return String(a.wo_number || '').localeCompare(String(b.wo_number || ''), undefined, { numeric: true });
}

const SORT_COMPARATORS = {
  due_date_asc: (a, b) => {
    if (!a.due_date && !b.due_date) return itemNameCompare(a, b);
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    const d = new Date(a.due_date) - new Date(b.due_date);
    return d !== 0 ? d : itemNameCompare(a, b);
  },
  due_date_desc: (a, b) => {
    if (!a.due_date && !b.due_date) return itemNameCompare(a, b);
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    const d = new Date(b.due_date) - new Date(a.due_date);
    return d !== 0 ? d : itemNameCompare(a, b);
  },
  item_asc: itemNameCompare,
  wo_number: (a, b) => String(a.wo_number || '').localeCompare(String(b.wo_number || ''), undefined, { numeric: true }),
  qty_desc: (a, b) => (Number(b.quantity_to_produce) || 0) - (Number(a.quantity_to_produce) || 0),
};

// Date banner colouring - mirrors the shop floor terminal's urgency scale.
function dateBannerStyle(dateKey) {
  if (!dateKey || dateKey === 'no-date') {
    return { bg: 'grey.600', label: null };
  }
  const days = Math.ceil((new Date(dateKey) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { bg: 'error.main', label: 'OVERDUE' };
  if (days === 0) return { bg: 'error.main', label: 'TODAY' };
  if (days === 1) return { bg: 'warning.main', label: 'TOMORROW' };
  if (days <= 7) return { bg: 'info.main', label: null };
  return { bg: 'grey.700', label: null };
}

function dateKeyOf(dueDate) {
  return dueDate ? String(dueDate).slice(0, 10) : 'no-date';
}

function dateLabelOf(dateKey) {
  if (dateKey === 'no-date') return 'No due date';
  return new Date(dateKey).toLocaleDateString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function readStoredMode() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'individual' || v === 'consolidated') return v;
  } catch (_) { /* ignore */ }
  return 'individual';
}

function writeStoredMode(v) {
  try { window.localStorage.setItem(STORAGE_KEY, v); } catch (_) { /* ignore */ }
}

// Filters survive menu switches for the session (same pattern as the
// Transaction History page) - the page unmounts on navigation and users kept
// losing what they'd typed.
function readStoredFilters() {
  try {
    return JSON.parse(window.sessionStorage.getItem(FILTERS_KEY)) || {};
  } catch (_) {
    return {};
  }
}

export default function WorkOrderManagementV2() {
  usePageTitle('Work Orders V2');
  // Filter inputs (server-side filtering via query params), restored from
  // sessionStorage so switching menus keeps them.
  const [search, setSearch] = useState(() => readStoredFilters().search || '');
  const [customerOrder, setCustomerOrder] = useState(() => readStoredFilters().customerOrder || '');
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = readStoredFilters();
    return 'statusFilter' in s ? s.statusFilter : 'active';
  });
  const [tree, setTree] = useState(() => readStoredFilters().tree || '');
  const [topLevelOnly, setTopLevelOnly] = useState(() => !!readStoredFilters().topLevelOnly);
  const [dueFrom, setDueFrom] = useState(() => readStoredFilters().dueFrom || '');
  const [dueTo, setDueTo] = useState(() => readStoredFilters().dueTo || '');

  // Text inputs are debounced so typing doesn't fire a request per keystroke.
  // Seeded from storage so the first fetch after a remount is already filtered.
  const [debouncedText, setDebouncedText] = useState(() => {
    const s = readStoredFilters();
    return {
      search: (s.search || '').trim(),
      customerOrder: (s.customerOrder || '').trim(),
      tree: (s.tree || '').trim(),
    };
  });

  const [viewMode, setViewMode] = useState(readStoredMode);
  const [groupByDate, setGroupByDate] = useState(() => {
    try { return window.localStorage.getItem(GROUP_BY_DATE_KEY) !== 'false'; } catch (_) { return true; }
  });
  const [sortOrder, setSortOrder] = useState(() => {
    try {
      const v = window.localStorage.getItem(SORT_KEY);
      return SORT_OPTIONS.some((o) => o.value === v) ? v : 'due_date_asc';
    } catch (_) { return 'due_date_asc'; }
  });
  const [drawerWoId, setDrawerWoId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [editWo, setEditWo] = useState(null);
  const [deleteWo, setDeleteWo] = useState(null);
  const [undoWo, setUndoWo] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isStaff = !!user?.is_staff;

  // Debounce the free-text filters.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedText({
        search: search.trim(),
        customerOrder: customerOrder.trim(),
        tree: tree.trim(),
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search, customerOrder, tree]);

  // Persist every filter input so navigation away and back restores them.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify({
        search, customerOrder, statusFilter, tree, topLevelOnly, dueFrom, dueTo,
      }));
    } catch (_) { /* storage unavailable - keeping filters is best-effort */ }
  }, [search, customerOrder, statusFilter, tree, topLevelOnly, dueFrom, dueTo]);

  const params = useMemo(() => {
    const p = {};
    if (debouncedText.customerOrder) {
      // Humans type the order number ("SO-6357"); a plain integer is treated
      // as the raw DB id so power users and deep links keep working.
      if (/^\d+$/.test(debouncedText.customerOrder)) {
        p.customer_order = debouncedText.customerOrder;
      } else {
        p.customer_order_number = debouncedText.customerOrder;
      }
    }
    if (debouncedText.search) p.search = debouncedText.search;
    // 'active' is filtered client-side (the API takes one concrete status).
    if (statusFilter && statusFilter !== 'active') p.status = statusFilter;
    if (debouncedText.tree) p.tree = debouncedText.tree;
    if (topLevelOnly) p.is_top_level = 'true';
    if (dueFrom) p.due_from = dueFrom;
    if (dueTo) p.due_to = dueTo;
    return p;
  }, [debouncedText, statusFilter, topLevelOnly, dueFrom, dueTo]);

  const { workOrders, loading, error, refresh, setParams } = useWOv2(params);

  // Push filter changes into the hook
  useEffect(() => { setParams(params); }, [params, setParams]);

  const handleViewModeChange = (_e, mode) => {
    if (mode == null) return;
    setViewMode(mode);
    writeStoredMode(mode);
  };

  const handleGroupByDateToggle = () => {
    setGroupByDate((v) => {
      try { window.localStorage.setItem(GROUP_BY_DATE_KEY, String(!v)); } catch (_) { /* ignore */ }
      return !v;
    });
  };

  const handleSortChange = (v) => {
    setSortOrder(v);
    try { window.localStorage.setItem(SORT_KEY, v); } catch (_) { /* ignore */ }
  };

  const displayedWorkOrders = useMemo(
    () => (statusFilter === 'active'
      ? workOrders.filter((wo) => ACTIVE_STATUSES.includes(wo.status))
      : workOrders),
    [workOrders, statusFilter],
  );

  const sortedWorkOrders = useMemo(() => {
    const arr = [...displayedWorkOrders];
    // Under date banners a due-date sort has nothing to say within a day -
    // fall back to item A-Z (same behaviour as the shop floor terminal).
    const cmp = groupByDate && (sortOrder === 'due_date_asc' || sortOrder === 'due_date_desc')
      ? SORT_COMPARATORS.due_date_asc
      : SORT_COMPARATORS[sortOrder] || SORT_COMPARATORS.due_date_asc;
    arr.sort(cmp);
    return arr;
  }, [displayedWorkOrders, sortOrder, groupByDate]);

  const groups = useMemo(
    () => (viewMode === 'consolidated' ? groupForConsolidatedView(sortedWorkOrders) : []),
    [viewMode, sortedWorkOrders],
  );

  // Date sections: [{key, label, style, wos}] for individual mode, or
  // [{key, label, style, groups}] for consolidated mode. Days follow the
  // due-date sort direction; "no due date" always sinks to the bottom.
  const dateSections = useMemo(() => {
    if (!groupByDate) return null;
    const dir = sortOrder === 'due_date_desc' ? -1 : 1;
    const map = new Map();
    const push = (key, entryKey, entry) => {
      if (!map.has(key)) {
        map.set(key, {
          key, label: dateLabelOf(key), style: dateBannerStyle(key), wos: [], groups: [],
        });
      }
      map.get(key)[entryKey].push(entry);
    };
    if (viewMode === 'consolidated') {
      groups.forEach((g) => push(dateKeyOf(g.due_date), 'groups', g));
    } else {
      sortedWorkOrders.forEach((wo) => push(dateKeyOf(wo.due_date), 'wos', wo));
    }
    return [...map.values()].sort((a, b) => {
      if (a.key === 'no-date') return 1;
      if (b.key === 'no-date') return -1;
      return dir * a.key.localeCompare(b.key);
    });
  }, [groupByDate, viewMode, sortedWorkOrders, groups, sortOrder]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch(''); setCustomerOrder(''); setStatusFilter('active');
    setTree(''); setTopLevelOnly(false); setDueFrom(''); setDueTo('');
  };

  const hasActiveFilters = search || customerOrder || statusFilter !== 'active' || tree || topLevelOnly || dueFrom || dueTo;

  const handleRefresh = async () => {
    try { await refresh(); toast.success('Refreshed'); }
    catch (_) { toast.error('Refresh failed'); }
  };

  // Per-card actions: "open order" for everyone (the most common jump when
  // tracking WOs against orders), Edit / Delete for staff only.
  const renderCardActions = (wo) => {
    const openOrder = wo.customer_order != null && (
      <Tooltip title={`Open order${wo.customer_order_number ? ` ${wo.customer_order_number}` : ''}`}>
        <IconButton
          size="small"
          aria-label="open customer order"
          data-testid={`wo-open-order-${wo.id}`}
          onClick={() => navigate(`/manufacturing/customer-orders/${wo.customer_order}`)}
        >
          <Launch fontSize="small" />
        </IconButton>
      </Tooltip>
    );
    if (!isStaff) return openOrder || null;
    return (
      <>
        {openOrder}
        <IconButton
          size="small"
          aria-label="edit work order"
          data-testid={`wo-edit-${wo.id}`}
          onClick={() => setEditWo(wo)}
        >
          <Edit fontSize="small" />
        </IconButton>
        {wo.status === 'completed' && (
          <Tooltip title="Undo completion — put back to planned">
            <IconButton
              size="small"
              color="warning"
              aria-label="undo completion"
              data-testid={`wo-undo-${wo.id}`}
              onClick={() => setUndoWo(wo)}
            >
              <Undo fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          color="error"
          aria-label="delete work order"
          data-testid={`wo-delete-${wo.id}`}
          onClick={() => setDeleteWo(wo)}
        >
          <Delete fontSize="small" />
        </IconButton>
      </>
    );
  };

  // Quick due-date chips - one-click ranges for the questions people
  // actually ask ("what's late?", "what's due today / this week?").
  const overdueActive = !dueFrom && dueTo === todayISO(-1);
  const todayActive = dueFrom === todayISO() && dueTo === todayISO();
  const weekActive = dueFrom === todayISO() && dueTo === todayISO(7);
  const applyQuickRange = (from, to, active) => {
    setDueFrom(active ? '' : from);
    setDueTo(active ? '' : to);
  };

  // Shared renderer for a consolidated group row (used flat and under banners).
  const renderGroupRow = (g) => {
    const isExpanded = expandedGroups.has(g.key);
    return (
      <Paper key={g.key} variant="outlined" sx={{ p: 1.5 }} data-testid={`group-row-${g.key}`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => toggleGroup(g.key)} aria-label="expand group">
            {isExpanded ? <ExpandMore /> : <KeyboardArrowRight />}
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {g.output_item_code} — {g.output_item_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {g.operation_code ? `${g.operation_code} · ` : ''}
              Due {g.due_date || '—'} · Total qty {g.total_quantity} · {g.source_count} WO{g.source_count === 1 ? '' : 's'}
            </Typography>
          </Box>
          {g.statuses.map((s) => (
            <Chip key={s} size="small" label={s} sx={{ ml: 0.5 }} />
          ))}
        </Box>
        <Collapse in={isExpanded} unmountOnExit>
          <Stack spacing={1} sx={{ mt: 1, pl: 4 }} data-testid={`group-sources-${g.key}`}>
            {g.sources.map((src) => {
              // Map back to a wo-card-shaped object for re-use.
              const wo = workOrders.find((w) => w.id === src.work_order_id);
              return wo ? (
                <WOv2Card
                  key={src.work_order_id}
                  wo={wo}
                  compact
                  onOpen={() => setDrawerWoId(wo.id)}
                  actions={renderCardActions(wo)}
                />
              ) : null;
            })}
          </Stack>
        </Collapse>
      </Paper>
    );
  };

  // Coloured day banner (mirrors the shop floor's date sections).
  const renderDateBanner = (section, count, units = 0) => (
    <Box
      sx={{
        bgcolor: section.style.bg,
        color: '#fff',
        py: 0.75,
        px: 2,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
      data-testid={`date-banner-${section.key}`}
    >
      <CalendarMonth fontSize="small" />
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {section.label}
      </Typography>
      {section.style.label && (
        <Chip size="small" label={section.style.label} sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700 }} />
      )}
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption">
        {count} {count === 1 ? 'entry' : 'entries'}
        {units > 0 ? ` · ${units} units` : ''}
      </Typography>
    </Box>
  );

  // ── Rendering ────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">Work Orders</Typography>
        <GenerationModeBadge />
        <Box sx={{ flex: 1 }} />
        <MigrateFromV1Button onMigrated={() => refresh()} />
        <TextField
          select
          label="Sort by"
          size="small"
          value={sortOrder}
          onChange={(e) => handleSortChange(e.target.value)}
          sx={{ minWidth: 160 }}
          SelectProps={{ inputProps: { 'data-testid': 'sort-order' } }}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Button
          variant={groupByDate ? 'contained' : 'outlined'}
          size="small"
          startIcon={<CalendarMonth />}
          onClick={handleGroupByDateToggle}
          data-testid="group-by-date-toggle"
        >
          By Date
        </Button>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          aria-label="view mode"
          data-testid="view-mode-toggle"
        >
          <ToggleButton value="individual" data-testid="view-mode-individual">Individual</ToggleButton>
          <ToggleButton value="consolidated" data-testid="view-mode-consolidated">Consolidated</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} size="small">
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Search WO / item code / name"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="WO2-001513, E/AB40009..."
              inputProps={{ 'data-testid': 'filter-search' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Customer order"
              size="small"
              fullWidth
              value={customerOrder}
              onChange={(e) => setCustomerOrder(e.target.value)}
              placeholder="SO-6357"
              inputProps={{ 'data-testid': 'filter-customer-order' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ inputProps: { 'data-testid': 'filter-status' } }}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Due from"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dueFrom}
              onChange={(e) => setDueFrom(e.target.value)}
              inputProps={{ 'data-testid': 'filter-due-from' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Due to"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dueTo}
              onChange={(e) => setDueTo(e.target.value)}
              inputProps={{ 'data-testid': 'filter-due-to' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Tree ID (advanced)"
              size="small"
              fullWidth
              value={tree}
              onChange={(e) => setTree(e.target.value)}
              inputProps={{ 'data-testid': 'filter-tree' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant={topLevelOnly ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setTopLevelOnly((v) => !v)}
              data-testid="filter-top-level"
            >
              {topLevelOnly ? 'Top-level only ✓' : 'Top-level only'}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">Quick:</Typography>
              <Chip
                size="small"
                label="Overdue"
                color={overdueActive ? 'error' : 'default'}
                variant={overdueActive ? 'filled' : 'outlined'}
                onClick={() => applyQuickRange('', todayISO(-1), overdueActive)}
                data-testid="quick-overdue"
              />
              <Chip
                size="small"
                label="Due today"
                color={todayActive ? 'error' : 'default'}
                variant={todayActive ? 'filled' : 'outlined'}
                onClick={() => applyQuickRange(todayISO(), todayISO(), todayActive)}
                data-testid="quick-today"
              />
              <Chip
                size="small"
                label="Next 7 days"
                color={weekActive ? 'info' : 'default'}
                variant={weekActive ? 'filled' : 'outlined'}
                onClick={() => applyQuickRange(todayISO(), todayISO(7), weekActive)}
                data-testid="quick-week"
              />
              {hasActiveFilters && (
                <Button
                  variant="text" size="small" startIcon={<Clear />} onClick={clearFilters}
                  data-testid="clear-filters"
                >
                  Clear filters
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} data-testid="loading-state">
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="error-state">
          Failed to load work orders.
          <Button onClick={refresh} size="small" sx={{ ml: 1 }}>Retry</Button>
        </Alert>
      )}

      {!loading && !error && sortedWorkOrders.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }} data-testid="empty-state">
          <Typography variant="h6">No work orders match these filters.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {statusFilter === 'active' && workOrders.length > 0
              ? `${workOrders.length} finished work order${workOrders.length === 1 ? '' : 's'} hidden - set Status to "All statuses" to see them.`
              : 'Work orders are created when a customer order is sent to production (exploded).'}
          </Typography>
        </Paper>
      )}

      {!loading && !error && sortedWorkOrders.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="results-count">
          {sortedWorkOrders.length} work order{sortedWorkOrders.length === 1 ? '' : 's'}
          {viewMode === 'consolidated' ? ` in ${groups.length} group${groups.length === 1 ? '' : 's'}` : ''}
          {(() => {
            const counts = {};
            sortedWorkOrders.forEach((wo) => { counts[wo.status] = (counts[wo.status] || 0) + 1; });
            const parts = ['planned', 'released', 'in_progress', 'completed', 'cancelled']
              .filter((s) => counts[s])
              .map((s) => `${counts[s]} ${s.replace('_', ' ')}`);
            return parts.length > 1 ? ` - ${parts.join(' · ')}` : '';
          })()}
        </Typography>
      )}

      {!loading && !error && sortedWorkOrders.length > 0 && viewMode === 'individual' && (
        <Stack spacing={1} data-testid="individual-list">
          {dateSections ? (
            dateSections.map((section) => (
              <React.Fragment key={section.key}>
                {renderDateBanner(
                  section,
                  section.wos.length,
                  Math.round(section.wos.reduce((acc, w) => acc + (Number(w.quantity_to_produce) || 0), 0)),
                )}
                {section.wos.map((wo) => (
                  <WOv2Card
                    key={wo.id}
                    wo={wo}
                    onOpen={() => setDrawerWoId(wo.id)}
                    actions={renderCardActions(wo)}
                  />
                ))}
              </React.Fragment>
            ))
          ) : (
            sortedWorkOrders.map((wo) => (
              <WOv2Card
                key={wo.id}
                wo={wo}
                onOpen={() => setDrawerWoId(wo.id)}
                actions={renderCardActions(wo)}
              />
            ))
          )}
        </Stack>
      )}

      {!loading && !error && sortedWorkOrders.length > 0 && viewMode === 'consolidated' && (
        <Stack spacing={1} data-testid="consolidated-list">
          {dateSections ? (
            dateSections.map((section) => (
              <React.Fragment key={section.key}>
                {renderDateBanner(
                  section,
                  section.groups.length,
                  Math.round(section.groups.reduce((acc, g) => acc + (Number(g.total_quantity) || 0), 0)),
                )}
                {section.groups.map((g) => renderGroupRow(g))}
              </React.Fragment>
            ))
          ) : (
            groups.map((g) => renderGroupRow(g))
          )}
        </Stack>
      )}

      <WOv2DetailDrawer
        open={drawerWoId != null}
        workOrderId={drawerWoId}
        onClose={() => setDrawerWoId(null)}
        onCancelled={() => refresh()}
        variant="dialog"
      />

      {isStaff && (
        <>
          <AdminWOEditDialogV2
            open={editWo != null}
            wo={editWo}
            onClose={() => setEditWo(null)}
            onSaved={() => { toast.success('Work order updated'); refresh(); }}
          />
          <AdminWOUndoCompletionDialogV2
            open={undoWo != null}
            workOrderId={undoWo?.id}
            woNumber={undoWo?.wo_number}
            onClose={() => setUndoWo(null)}
            onUndone={(r) => {
              toast.success(`${r.wo_number} put back to planned — ${r.quantity_removed} removed from stock`);
              refresh();
            }}
          />
          <AdminWODeleteDialogV2
            open={deleteWo != null}
            wo={deleteWo}
            onClose={() => setDeleteWo(null)}
            onDeleted={(report) => {
              if (report && typeof report === 'object' && report.deleted_work_orders != null) {
                toast.success(`Deleted ${report.deleted_work_orders} work order(s)`);
              } else {
                toast.success('Work order deleted');
              }
              refresh();
            }}
          />
        </>
      )}
    </Box>
  );
}
